/**
 * 站点公共工具
 *
 * 收敛 Header / Footer / ContactUs / Home / ArticleList 里重复的逻辑：
 * 站点配置读取、图片地址拼接、导航名称映射、富文本处理。
 */

/**
 * 后台「系统配置」按列表顺序返回数组，索引会随后台增删而错位。
 * 这里把索引集中成常量，改后台顺序时只需改这一处。
 */
export const CONFIG_INDEX = {
  COMPANY_NAME: 0,     // 公司名称
  ICP: 1,              // ICP 备案号
  COMPANY_DESC: 4,     // 公司简介
  EMAIL: 5,            // 电子邮箱
  ADDRESS: 6,          // 公司地址
  WORK_TIME: 7,        // 工作时间
  PHONE: 8,            // 联系电话
  LOGO: 9,             // Logo 图片地址
  CONTACT_CONTENT: 11  // 联系我们富文本内容 / 友情链接 JSON
}

/**
 * 后台「产品分类」里存的是英文名，中文站展示时做一次映射。
 * 英文站直接使用后台原始名称，不做映射。
 */
export const NAV_NAME_MAP = {
  'Home': '首页',
  'Company': '关于我们',
  'Sales Department': '销售部门',
  'Technical': '技术中心',
  'The latest': '最新动态',
  'contact': '联系我们',
  'Contact': '联系我们',
  'preparation': '公司简介',
  'UK Branch': '英国分公司',
  'service': '服务中心',
  'report': '新闻动态',
  'qualification': '资质荣誉',
  'us': '关于我们',
  'Product': '产品中心',
  'product': '产品中心',
  'News': '新闻动态',
  'news': '新闻动态',
  'About': '关于我们',
  'about': '关于我们'
}

/**
 * 安全读取站点配置
 * 后台配置返回结构变化过：
 *   旧版：稀疏对象/数组（键是配置 id，需按 CONFIG_INDEX 索引读）
 *   新版：压缩数组（0~n 连续，每项带稳定 name 字段，索引已错位）
 * 因此这里做双兼容：数组结构优先按 name 精确匹配；匹配不上再退回旧索引。
 * @param {Array|Object} config 配置数组/对象
 * @param {Number} index 旧索引（CONFIG_INDEX 常量），同时用于推导该字段的 name
 * @param {String} fallback 取不到时的默认值
 */
// 旧索引 → 后端配置 name 的对照（后端 name 命名稳定，索引会变但 name 不变）
const CONFIG_NAME_BY_INDEX = {
  0: 'name',        // 站点名（name 字段名就叫 name）
  1: 'beian',       // ICP
  6: 'address',     // 公司地址
  8: 'mobile',      // 联系电话（后端字段是 mobile 不是 phone）
  9: 'Logo',        // Logo 图片
  11: 'Contact'     // 联系我们富文本
}

export function getConfigValue(config, index, fallback) {
  const fb = fallback === undefined ? '' : fallback
  if (!config) return fb
  // 新版：压缩数组 → 优先按 name 匹配
  if (Array.isArray(config)) {
    const targetName = CONFIG_NAME_BY_INDEX[index]
    if (targetName) {
      const item = config.find(i => i && i.name === targetName)
      if (item && item.value !== undefined && item.value !== '') return item.value
    }
    return fb
  }
  // 旧版：稀疏对象按索引
  const item = config[index]
  const value = item && item.value
  return value ? value : fb
}

/**
 * 按 name 字段在 config 中查找条目（不依赖索引顺序）
 * 后台新增配置项时只要 name 唯一即可，索引变动不影响前端
 * @param {Object|Array} config 配置对象/数组
 * @param {String} name 要查找的 name（如 'qrcode'、'wechat'）
 * @param {String} fallback 取不到时的默认值
 */
export function getConfigByName(config, name, fallback) {
  if (!config || typeof config !== 'object') return fallback === undefined ? '' : fallback
  const found = Object.values(config).find(item => item && item.name === name)
  if (found && found.value) return found.value
  return fallback === undefined ? '' : fallback
}

/**
 * 拼接图片地址
 * 后端若已返回完整 URL 就不再拼前缀，避免出现 https://xxxhttps://xxx 的破图
 * @param {String} baseUrl 图片域名前缀
 * @param {String} path 接口返回的图片字段
 */
export function fixUrl(baseUrl, path) {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  const base = (baseUrl || '').replace(/\/+$/, '')
  const suffix = String(path).replace(/^\/+/, '')
  return base + '/' + suffix
}

/**
 * 导航名称处理
 * @param {String} name 后台原始名称
 * @param {String} lang 当前语言 zh / en
 */
export function translateNavName(name, lang) {
  if (lang === 'en') return name
  return NAV_NAME_MAP[name] || name
}

/**
 * 去掉 HTML 标签，得到纯文本
 * 列表简介用它，避免富文本被 CSS 截断后出现未闭合标签撑破布局
 */
export function stripHtml(html) {
  if (!html) return ''
  return String(html)
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 生成纯文本摘要
 * @param {String} html 富文本
 * @param {Number} maxLen 最大字数
 */
export function toPlainSummary(html, maxLen) {
  const len = maxLen === undefined ? 100 : maxLen
  const text = stripHtml(html)
  if (!text) return ''
  return text.length > len ? text.slice(0, len) + '...' : text
}

/**
 * 富文本安全过滤
 * 详情页用 v-html 渲染，后台编辑器内容不可信，这里做一次白名单清洗：
 * 移除 script/iframe 等可执行标签、on* 事件属性、javascript: / vbscript: 伪协议；
 * src 上的 data: 仅放行图片（base64 内嵌图），href 上的 data: 一律禁止
 * @param {String} html 原始富文本
 */
export function sanitizeRichText(html) {
  if (!html) return ''
  let out = String(html)
  // 成对的可执行/危险标签整块移除
  out = out.replace(
    /<(script|style|iframe|object|embed|link|meta|form|input|button)[\s\S]*?<\/\1\s*>/gi,
    ''
  )
  // 单独的可执行/危险标签移除
  out = out.replace(
    /<\/?(script|style|iframe|object|embed|link|meta|form|input|button)[^>]*>/gi,
    ''
  )
  // 移除所有 on* 事件属性
  out = out.replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
  // 伪协议与 data: 处理：按属性名（href / src）区别对待
  // ⚠️ 2026-09-14 两处修正：
  //   ① 原实现把 data: 与 javascript: 一起无差别清除 → src="data:image/..." 整条属性被删、
  //      正文内嵌图变破图。现只对「非图片的 data:」动手。
  //   ② 原实现用 [^"'\s>]* 取值，遇到空格或 > 即止 → 值里含空格（如未编码的 SVG data URL）
  //      会删不干净、留下残渣。现改为「引号配对取完整值」再判断。
  out = out.replace(
    /\b(href|src)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi,
    function (whole, attr, raw) {
      const quoted = raw.length >= 2 && /^["']/.test(raw)
      const value = (quoted ? raw.slice(1, -1) : raw).trim().toLowerCase()
      // 伪协议：href / src 一律删
      if (/^(?:javascript|vbscript)\s*:/.test(value)) return ''
      if (/^data\s*:/.test(value)) {
        // href 上的 data: 一律删（顶层打开 data:image/svg+xml 可执行脚本，防钓鱼）
        if (attr.toLowerCase() === 'href') return ''
        // src 上的 data: 仅放行图片
        if (!/^data\s*:\s*image\//.test(value)) return ''
      }
      return whole
    }
  )
  return out
}

/**
 * 富文本展示处理
 *
 * ⚠️ 2026-09-14 变更：不再改写 <img> 的宽高与样式，保留后台富文本原有的图片格式。
 *
 * 旧实现会先抹掉图片的 style / width / height，再统一注入
 * style="max-width:100%;height:auto;display:block;margin:16px auto;"。
 * 好处是"图片一律自适应"，代价是后台设置的图片排版（百分比宽度、左右对齐、
 * 浮动图文环绕等）全部失效。现改为原样透传，排版交还给后台。
 *
 * 图片不超屏由各详情页容器的 CSS 兜底保证（四处均已具备）：
 *   ArticleDetail  .detail-body  /  About        .about-body
 *   ContactUs      .content-body /  TechnicalDetail .detail-body
 *   均为 :deep(img){ max-width:100%; height:auto; display:block }
 * 注意：兜底只能防"宽度撑破"，若后台给图片写了内联 max-width 或内联 height，
 * 仍需各处兜底加 !important 才能完全压住（详见工作区《富文本图片_超屏实测》）。
 *
 * 如需回退到"强制图片自适应"，取消下方注释块即可。
 *
 * @param {String} html 原始富文本
 */
export function formatRichText(html) {
  const newContent = sanitizeRichText(html)

  // ---- 原「强制图片自适应」逻辑，暂时停用（改为保留后台图片格式）----
  // let newContent = sanitizeRichText(html)
  // // 清掉图片上的固定宽高，改为自适应
  // newContent = newContent.replace(/<img[^>]*>/gi, function (match) {
  //   return match
  //     .replace(/style\s*=\s*"[^"]*"/gi, '')
  //     .replace(/style\s*=\s*'[^']*'/gi, '')
  //     .replace(/width\s*=\s*"[^"]*"/gi, '')
  //     .replace(/width\s*=\s*'[^']*'/gi, '')
  //     .replace(/height\s*=\s*"[^"]*"/gi, '')
  //     .replace(/height\s*=\s*'[^']*'/gi, '')
  // })
  // newContent = newContent.replace(/<img/gi, '<img style="max-width:100%;height:auto;display:block;margin:16px auto;"')
  // ---- 停用结束 ----

  return newContent
}
